<template>
  <div
    :ref="
      setupImpressionTracker({
        name: 'plp_reassurance_block',
        value: content.trackingName,
      })
    "
    class="relative flex h-[262px] flex-col overflow-hidden rounded-lg md:h-full md:flex-row md:items-stretch"
    :class="BACKGROUND_COLOR[content.variant]"
    :data-qa="`plp-blocks-${content.kind}`"
    :data-test="`plp-blocks-${content.kind}`"
  >
    <div
      class="flex grow flex-col justify-end gap-8 px-20 py-24 md:grow-0"
      :class="content.image && 'md:w-1/2'"
    >
      <RevIllustration
        v-if="icon?.kind === 'image'"
        alt=""
        :class="content.iconMobileOnly && 'md:hidden'"
        :height="icon.height ?? 40"
        :src="icon.src"
        :width="icon.width ?? 40"
      />
      <RevPill
        v-else-if="icon?.kind === 'component'"
        class="mood-inverse w-[42px] justify-center !p-0"
        :class="content.iconMobileOnly && 'md:hidden'"
        :prefix-icon="icon.src"
        size="large"
      />
      <p class="heading-3 text-static-default-hi">{{ content.title }}</p>
      <p v-if="content.description" class="body-2 text-static-default-hi">
        {{ content.description }}
      </p>
      <RevLink
        v-if="content.ctaLabel"
        class="body-1 w-fit"
        :to="content.ctaUrl"
        :underlined="true"
        @click="onClick"
      >
        {{ content.ctaLabel }}
      </RevLink>
    </div>
    <RevIllustration
      v-if="content.image"
      alt=""
      class="absolute inset-y-0 right-0 hidden h-full w-auto object-contain md:block"
      :height="content.image.height"
      :src="content.image.src"
      :width="content.image.width"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevPill } from '@ds/components/Pill'

import { useImpressionTracker } from '../../../../composables/useImpressionTracker'
import type { PlpBlockContent } from '../../../../composables/usePlpBlocks/usePlpBlocks'

const BACKGROUND_COLOR = {
  brandMid: 'bg-static-brand-mid',
  brandLow: 'bg-static-brand-low',
  defaultMid: 'bg-static-default-mid',
} as const

const props = defineProps<{
  content: PlpBlockContent
}>()

const icon = computed(() => {
  const { icon: contentIcon } = props.content

  if (!contentIcon) return undefined

  return typeof contentIcon.src === 'string'
    ? {
        kind: 'image' as const,
        src: contentIcon.src,
        width: contentIcon.width,
        height: contentIcon.height,
      }
    : { kind: 'component' as const, src: contentIcon.src }
})

const { trackClick, trackBlockImpression } = useTracking()
const { setupImpressionTracker } = useImpressionTracker(trackBlockImpression)

function onClick() {
  if (!props.content.ctaLabel) return
  trackClick({
    zone: 'plp_reassurance_block',
    name: props.content.trackingName,
    value: 'learn_more',
  })
}
</script>
